import { useId, type ReactNode } from "react";

/**
 * Gauge — a theme-aware circular arc dial (observability-ui Netdata-adaptation).
 *
 * The headline-metric dial that gives the Overview (and the section dashboards) the dense,
 * at-a-glance Netdata "gauge row" look, but styled entirely through the app's theme tokens so it
 * reads correctly in BOTH light and dark — there is no permanently-dark surface. It draws a 270°
 * arc: a faint full-sweep track, a value arc that fills proportionally from `min`→`max`, the value
 * big in the centre with its unit, and the min/max labels beneath the ends.
 *
 * Honesty (design 12.3): a `null` value renders an explicit "no reading" dial (empty track, em-dash
 * centre) rather than a fabricated zero. The arc colour is a redundant channel — the number is
 * always the primary signal — and defaults to the accent token; a caller can map thresholds onto
 * the status tokens (e.g. a deny-ratio or latency gauge goes warn/amber then error/red past a
 * cutoff) via `severity`. Reduced motion is respected: the fill has a CSS transition that the
 * theme's global reduced-motion rule neutralises.
 *
 * Presentational only: value in, no fetching. SVG scales with its container (viewBox).
 */

/** The arc's colour intent — mapped to a theme token by the CSS, never a literal colour. */
export type GaugeSeverity = "accent" | "ok" | "warn" | "error" | "unknown";

export interface GaugeProps {
  /** The metric label shown under the dial (e.g. "Deny ratio"). */
  readonly label: string;
  /** The current value. `null` renders the honest "no reading" dial. */
  readonly value: number | null;
  /** Lower bound of the arc (default 0). */
  readonly min?: number;
  /** Upper bound of the arc. Required for a meaningful fill; defaults to a sensible 100. */
  readonly max?: number;
  /** Unit shown next to the centre value (e.g. "/s", "ms", "%"). */
  readonly unit?: string;
  /** Pre-formatted display value; overrides the default numeric formatting when given. */
  readonly displayValue?: string;
  /** Arc colour intent (threshold mapping is the caller's job). Defaults to "accent". */
  readonly severity?: GaugeSeverity;
  /** A confidence marker shown by the value (e.g. "est.") — design 12.3. */
  readonly measure?: string;
}

/** Geometry: a 270° arc centred at the bottom, opening downward (−225°→+45°). */
const START_ANGLE = 135; // degrees, measured clockwise from the positive x-axis (SVG)
const SWEEP = 270; // total degrees the arc covers
const R = 54; // arc radius within the 140×140 viewBox
const CX = 70;
const CY = 70;

/** Polar→cartesian for a point on the arc at `angleDeg` (SVG y grows downward). */
function pointAt(angleDeg: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + R * Math.cos(rad), y: CY + R * Math.sin(rad) };
}

/** Build an SVG arc path from `fromDeg` to `toDeg` (clockwise) at radius R. */
function arcPath(fromDeg: number, toDeg: number): string {
  const start = pointAt(fromDeg);
  const end = pointAt(toDeg);
  const largeArc = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${R} ${R} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

export function Gauge(props: GaugeProps): ReactNode {
  const labelId = useId();
  const min = props.min ?? 0;
  const max = props.max ?? 100;
  const severity = props.severity ?? "accent";
  const hasReading = props.value !== null && Number.isFinite(props.value);

  // Clamp the fill fraction into [0,1]; a degenerate range reads as empty rather than NaN.
  const frac =
    hasReading && max > min
      ? Math.min(1, Math.max(0, ((props.value as number) - min) / (max - min)))
      : 0;

  const trackPath = arcPath(START_ANGLE, START_ANGLE + SWEEP);
  const valuePath = arcPath(START_ANGLE, START_ANGLE + SWEEP * frac);

  const centre = props.displayValue ?? (hasReading ? String(props.value) : "—");
  const ariaText = hasReading
    ? `${props.label}: ${centre}${props.unit ? ` ${props.unit}` : ""}`
    : `${props.label}: no reading`;

  const unit = hasReading && props.unit !== undefined ? props.unit.trim() : "";
  const measure = hasReading && props.measure !== undefined ? props.measure.trim() : "";

  return (
    <figure className="obs-gauge" role="group" aria-labelledby={labelId}>
      <svg
        className="obs-gauge__svg"
        viewBox="0 0 140 136"
        role="img"
        aria-label={ariaText}
        data-severity={hasReading ? severity : "unknown"}
      >
        {/* Faint full-sweep track (theme-tokened, dims correctly per theme). */}
        <path className="obs-gauge__track" d={trackPath} />
        {/* Value arc — only drawn when there is a real reading, so an empty dial is honestly empty. */}
        {hasReading && frac > 0 && <path className="obs-gauge__value" d={valuePath} />}

        {/* The value, unit and confidence live INSIDE the svg, centred on the dial, so they scale
            with the gauge and never overlap the arc regardless of how wide the number is. The value
            auto-fits its width via `textLength` so a long figure (e.g. "3,180") shrinks to fit
            inside the ring rather than spilling over it. */}
        <text
          className="obs-gauge__number"
          data-empty={hasReading ? undefined : "true"}
          x={CX}
          y={unit === "" ? CY + 4 : CY}
          textAnchor="middle"
          dominantBaseline="middle"
          textLength={fitLength(centre)}
          lengthAdjust="spacingAndGlyphs"
        >
          {centre}
        </text>
        {unit !== "" && (
          <text className="obs-gauge__unit" x={CX} y={CY + 16} textAnchor="middle">
            {unit}
          </text>
        )}
        {measure !== "" && (
          <text className="obs-gauge__measure" x={CX} y={CY + 29} textAnchor="middle">
            {measure}
          </text>
        )}

        {/* Min / max scale labels, placed clearly BELOW the open ends of the 270° arc (the arc ends
            sit at ~y=108; CY+56 = y=126 keeps the labels off the ring and legible). */}
        <text className="obs-gauge__scale" x={pointAt(START_ANGLE + SWEEP).x} y={CY + 56} textAnchor="middle">
          {formatBound(min)}
        </text>
        <text className="obs-gauge__scale" x={pointAt(START_ANGLE).x} y={CY + 56} textAnchor="middle">
          {formatBound(max)}
        </text>
      </svg>
      <figcaption id={labelId} className="obs-gauge__label">
        {props.label}
      </figcaption>
    </figure>
  );
}

/**
 * The max text width (in viewBox units) the centre value may occupy before `textLength` shrinks it,
 * so a wide figure never overruns the dial's inner diameter. Short values are left at their natural
 * width (return a length only when the string is long enough to risk overflow).
 */
function fitLength(text: string): number | undefined {
  // The dial's usable inner width is ~2*R minus the stroke; keep a margin so glyphs clear the arc.
  const MAX = 72;
  // A rough per-character width at the number font size; only clamp when the natural width exceeds MAX.
  const estimated = text.length * 12.5;
  return estimated > MAX ? MAX : undefined;
}

/** Compact bound label: integers as-is, otherwise a short fixed form. */
function formatBound(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(n < 1 ? 2 : 1);
}
